import { isProblemDetail } from "@roastery/api-client";
import { RequireAuth, useAuth } from "@roastery/auth";
import { useTranslation } from "@roastery/i18n";
import { Button } from "@roastery/ui";
import { Outlet, createFileRoute, useMatchRoute } from "@tanstack/react-router";
import { AnnouncementBanner } from "../components/announcement-banner";
import { CreditBlockNotice } from "../components/credit-block-notice";
import { FullScreenMessage } from "../components/full-screen-message";
import { PortalLegalLinks } from "../components/legal-links";
import { PortalHeader } from "../components/portal-header";
import { BrandingTheme } from "../components/roastery-mark";
import { SuspensionBanner } from "../components/suspension-banner";
import { usePublicBranding } from "../lib/branding";
import { usePortalIdentity } from "../lib/portal-api";
import { cartKeyOf, useCart } from "../lib/use-cart";

/**
 * The portal proper: everything a wholesale buyer signs in to see.
 *
 * <p>A pathless layout, so the addresses stay what they were — `/orders` is still `/orders`, not
 * `/_authed/orders`. Its whole job is the gate that used to sit in the root route, moved down one
 * level in US-24.4 so the public page under a QR can live beside it rather than behind it.
 */
export const Route = createFileRoute("/_authed")({
  component: PortalLayout,
});

/**
 * Sign in, then let the portal's own identity call decide whether there is a portal to show.
 *
 * <p>Before any of that, the address is asked whose portal this is (US-10.5). That answer paints the
 * screens a customer sees without a token — the wait on the way to Keycloak, and the two ways this
 * can end badly — and it is a hint, never an authority: the roastery the buyer actually belongs to
 * comes from the token, and repaints over this one as soon as it arrives.
 */
function PortalLayout() {
  const { t } = useTranslation();
  const auth = useAuth();
  const branding = usePublicBranding();
  const roastery = { name: branding.data?.name, logoUrl: branding.data?.logoUrl };

  return (
    <RequireAuth
      pending={<FullScreenMessage title={t("auth.signingIn")} roastery={roastery} testId="auth-pending" />}
      onError={(error) => (
        <FullScreenMessage
          testId="auth-error"
          title={t("auth.error")}
          description={error.message}
          roastery={roastery}
          action={
            <Button onClick={() => void auth.signinRedirect()} data-testid="retry-sign-in">
              {t("auth.signIn")}
            </Button>
          }
        />
      )}
    >
      <PortalShell fallbackRoastery={roastery} />
    </RequireAuth>
  );
}

/**
 * Everything past the login screen hangs off one call, {@code GET /api/v1/portal/me}, and its two
 * ways of failing are two different sentences.
 *
 * <p>Both frontends talk to the same realm, so a roastery's own staff can reach this address with a
 * perfectly valid token; they lack the portal permission and are told this is not their portal. A
 * buyer whose access the roastery has withdrawn holds the permission but has no row, and is told
 * that instead. Guessing from the role alone could not tell those apart, and the second is the one
 * a customer will actually ring up about.
 */
function PortalShell({ fallbackRoastery }: { fallbackRoastery: { name?: string; logoUrl?: string | null } }) {
  const { t } = useTranslation();
  const auth = useAuth();
  const identity = usePortalIdentity();
  const roastery = identity.data?.roastery;
  const creditBlock = identity.data?.creditBlock;
  const cart = useCart(cartKeyOf(identity.data?.portalUserId, identity.data?.customerId));
  const matchRoute = useMatchRoute();
  // A full cart carries the short version of the same sentence beside its button (US-37.9, L-11);
  // the footer under it would only say it twice.
  const legalInCart = Boolean(matchRoute({ to: "/cart" })) && cart.lines.length > 0;

  const signOut = (
    <Button variant="outline" data-testid="portal-sign-out" onClick={() => void auth.signoutRedirect()}>
      {t("header.signOut")}
    </Button>
  );

  if (identity.isLoading) {
    return <FullScreenMessage title={t("app.loading")} roastery={fallbackRoastery} testId="portal-pending" />;
  }
  if (identity.error) {
    const problem = isProblemDetail(identity.error) ? identity.error : undefined;
    const withdrawn = problem?.code === "SALES_PORTAL_NO_ACCESS";
    return (
      <FullScreenMessage
        testId={withdrawn ? "portal-no-access" : "portal-not-a-customer"}
        title={withdrawn ? t("portal.noAccess.title") : t("portal.notACustomer.title")}
        description={withdrawn ? t("portal.noAccess.description") : t("portal.notACustomer.description")}
        roastery={fallbackRoastery}
        action={signOut}
      />
    );
  }

  return (
    <div className="flex h-full flex-col" data-testid="portal-shell">
      {/* The token's answer, which overwrites whatever the address claimed. */}
      <BrandingTheme name={roastery?.name} logoUrl={roastery?.logoUrl} primaryColor={roastery?.primaryColor} />
      <PortalHeader />
      {roastery?.suspended ? <SuspensionBanner roasteryName={roastery.name} /> : null}
      <AnnouncementBanner />
      {/* Told before they fill a cart, not after (US-25.3): the cart's button is disabled for the
          same reason, and a buyer who learns it only there has done the work for nothing. */}
      {creditBlock ? (
        <div className="shrink-0 border-b border-border px-4 py-2 md:px-6">
          <CreditBlockNotice block={creditBlock} testId="portal-credit-banner" />
        </div>
      ) : null}
      <main className="flex flex-1 flex-col overflow-y-auto p-4 md:p-6" data-testid="portal-main">
        <div className="flex-1">
          <Outlet />
        </div>
        {/* The two names a buyer is owed: the roastery, which decides, and us, who hold the rows on
            its behalf (US-37.9, L-11). Every screen of the portal, because RODO art. 13 is not a
            thing to hide one click away — but inside the scrolling area, so on a phone it does not
            take a strip of the screen for good. */}
        {legalInCart ? null : (
          <footer className="mt-6 border-t border-border pt-3" data-testid="portal-footer">
            <PortalLegalLinks testId="portal-legal" />
          </footer>
        )}
      </main>
    </div>
  );
}
